import { useEffect, useState } from "react";
import type { SlideProps } from "@/engine/app/offsite-config";
import { api } from "@/api/client";
import { useReadOnly, useStore } from "@/state/store";

interface Notiz {
  id: string;
  text: string;
  createdAt: number;
}

/**
 * Offene Diskussion — mit Live-Protokoll: Der Moderator hält Wortmeldungen
 * direkt auf der Leinwand fest. Die Punkte liegen dauerhaft im Backend
 * (GET /notizen) und sind damit die Dokumentation der Runde.
 */
export function Slide18SorgenNoete(_: SlideProps) {
  const { state } = useStore();
  const readOnly = useReadOnly();
  const [notizen, setNotizen] = useState<Notiz[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.notizen.list(state.auth.token);
        if (!cancelled) setNotizen(r.notizen);
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [state.auth.token]);

  const add = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.notizen.add(state.auth.token, text);
      setNotizen((n) => [...n, r.notiz]);
      setDraft("");
    } catch (err) {
      console.error(err);
      setError("Konnte den Punkt nicht speichern.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setError(null);
    try {
      await api.notizen.remove(state.auth.token, id);
      setNotizen((n) => n.filter((x) => x.id !== id));
    } catch (err) {
      console.error(err);
      setError("Konnte den Punkt nicht löschen.");
    }
  };

  return (
    <div className="deck-root dark" style={{ background: "var(--svo-dunkelblau)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <div
        className="float"
        aria-hidden="true"
        style={{
          width: 360,
          height: 360,
          top: "-100px",
          left: "-100px",
          background: "var(--svo-blau)",
          opacity: 0.22,
        }}
      />

      <header className="deck-head" style={{ textAlign: "center", margin: "0 auto" }}>
        <div className="deck-eyebrow rise rise-1">Offene Diskussion</div>
        <h1 className="deck-title rise rise-2" style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.6rem)" }}>
          Eure Fragen. <span className="accent">Eure Themen.</span>
        </h1>
        <p className="deck-sub rise rise-3" style={{ margin: "0 auto" }}>
          Kein Vortrag mehr — jetzt reden wir. Was wir festhalten, steht
          sofort hier auf der Leinwand.
        </p>
      </header>

      <div
        className="rise rise-4"
        style={{
          width: "min(900px, 88%)",
          margin: "1rem auto 0",
          display: "grid",
          gap: 10,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        {notizen.length === 0 && (
          <div
            style={{
              textAlign: "center",
              color: "rgba(255,255,255,0.45)",
              fontStyle: "italic",
              padding: "1.5rem 0",
            }}
          >
            Noch nichts festgehalten — was beschäftigt euch?
          </div>
        )}
        {notizen.map((n, i) => (
          <div
            key={n.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "12px 18px",
              borderRadius: "var(--radius-xl)",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <span
              style={{
                color: "var(--svo-blau)",
                fontWeight: 600,
                fontSize: 14,
                minWidth: "2ch",
              }}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <span style={{ flex: 1, fontSize: "clamp(1rem, 1.6vw, 1.3rem)", color: "#fff" }}>
              {n.text}
            </span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => remove(n.id)}
                title="Punkt löschen"
                style={{
                  border: "none",
                  background: "transparent",
                  color: "rgba(255,255,255,0.4)",
                  cursor: "pointer",
                  fontSize: 15,
                }}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (
        <div
          className="rise rise-5"
          style={{
            width: "min(900px, 88%)",
            margin: "0.75rem auto 0",
            display: "flex",
            gap: 10,
          }}
        >
          <input
            className="themen-card-input"
            style={{ fontSize: "clamp(1rem, 1.5vw, 1.25rem)", padding: "12px 16px" }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Wortmeldung festhalten … (Enter)"
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
          />
          <button
            type="button"
            className="btn btn-success"
            onClick={add}
            disabled={!draft.trim() || busy}
          >
            {busy ? "…" : "✓ Festhalten"}
          </button>
        </div>
      )}
      {error && (
        <div style={{ textAlign: "center", color: "var(--svo-error, #e5484d)", marginTop: 6 }}>
          {error}
        </div>
      )}
    </div>
  );
}
